/**
 * ArtifactItemsHierarchy Component
 * 
 * Displays artifact items (improvement and operational) in a hierarchical tree view.
 */

import React, { useState, useMemo } from 'react';
import { Tree, Card, Tag, Progress, Tooltip, Badge, Empty, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import {
  RocketOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  FolderOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { ArtifactItemWithHierarchy } from '../../../types/objective';
import styles from './ArtifactItemsHierarchy.module.css';

const { Text, Title } = Typography;

interface ArtifactItemsHierarchyProps {
  improvementItems: ArtifactItemWithHierarchy[];
  operationalItems: ArtifactItemWithHierarchy[];
  onItemClick?: (item: ArtifactItemWithHierarchy) => void;
  className?: string;
  compact?: boolean;
}

// Format date for display
function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric'
  });
}

// Get status info for an item
function getStatusInfo(item: ArtifactItemWithHierarchy): { 
  color: string; 
  icon: React.ReactNode; 
  label: string;
  progress: number;
} {
  if (item.is_validated) {
    return { 
      color: 'success', 
      icon: <CheckCircleOutlined />, 
      label: 'Validated',
      progress: 100
    };
  }
  if (item.item_type === 'improvement') {
    return { 
      color: 'processing', 
      icon: <SyncOutlined spin />, 
      label: 'In Progress',
      progress: 50
    };
  }
  return { 
    color: 'default', 
    icon: <ClockCircleOutlined />, 
    label: 'Active',
    progress: 30
  };
}

// Item node component
const ItemNode: React.FC<{ item: ArtifactItemWithHierarchy; compact?: boolean }> = ({ item, compact }) => {
  const status = getStatusInfo(item);
  const isImprovement = item.item_type === 'improvement';
  
  return (
    <div className={`${styles.itemNode} ${compact ? styles.compact : ''}`}>
      <div className={styles.itemContent}>
        <span className={styles.itemIcon}>
          {isImprovement ? (
            <RocketOutlined style={{ color: '#1890ff' }} />
          ) : (
            <SettingOutlined style={{ color: '#52c41a' }} />
          )}
        </span>
        <div className={styles.itemInfo}>
          <span className={styles.itemName}>{item.name}</span>
          {!compact && item.description && (
            <Text type="secondary" className={styles.itemDescription} ellipsis>
              {item.description}
            </Text>
          )}
        </div>
        <div className={styles.itemMeta}>
          <Tag color={status.color} icon={status.icon} className={styles.statusTag}>
            {status.label}
          </Tag>
          {!compact && (
            <>
              {item.effective_start_date && (
                <span className={styles.dateRange}>
                  {formatDate(item.effective_start_date)}
                  {item.effective_end_date && ` - ${formatDate(item.effective_end_date)}`}
                </span>
              )}
              {item.version_number && (
                <Badge 
                  count={`v${item.version_number}`} 
                  style={{ backgroundColor: '#8c8c8c' }}
                />
              )}
            </>
          )}
        </div>
      </div>
      {!compact && (
        <Progress 
          percent={status.progress} 
          size="small" 
          showInfo={false}
          strokeColor={status.color === 'success' ? '#52c41a' : '#1890ff'}
          className={styles.itemProgress}
        />
      )}
    </div>
  );
};

// Build tree data from items
function buildTreeData(
  items: ArtifactItemWithHierarchy[],
  type: 'improvement' | 'operational',
  compact?: boolean
): DataNode[] {
  // Group items by artifact
  const artifactGroups: Record<string, ArtifactItemWithHierarchy[]> = {};
  
  items.forEach(item => {
    const artifactId = item.artifact_info?.artifact_id || item.artifact_ref;
    if (!artifactGroups[artifactId]) {
      artifactGroups[artifactId] = [];
    }
    artifactGroups[artifactId].push(item);
  });
  
  // Build tree nodes
  const nodes: DataNode[] = Object.entries(artifactGroups).map(([artifactId, groupItems]) => {
    const firstItem = groupItems[0];
    const artifactName = firstItem.artifact_info?.artifact_name || 'Unknown Artifact';
    
    return {
      key: `artifact-${artifactId}`,
      title: (
        <div className={styles.artifactNode}>
          <FolderOutlined className={styles.folderIcon} />
          <span className={styles.artifactName}>{artifactName}</span>
          <Badge count={groupItems.length} style={{ marginLeft: 8 }} />
        </div>
      ),
      children: groupItems.map(item => ({
        key: item.id,
        title: <ItemNode item={item} compact={compact} />,
        icon: <FileOutlined />,
        isLeaf: true,
      })),
    };
  });
  
  return nodes;
}

export const ArtifactItemsHierarchy: React.FC<ArtifactItemsHierarchyProps> = ({
  improvementItems,
  operationalItems,
  onItemClick,
  className,
  compact = false,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  
  // Build tree data
  const improvementTreeData = useMemo(
    () => buildTreeData(improvementItems, 'improvement', compact),
    [improvementItems, compact]
  );
  
  const operationalTreeData = useMemo(
    () => buildTreeData(operationalItems, 'operational', compact),
    [operationalItems, compact]
  );
  
  // Handle tree expand
  const onExpand = (expandedKeysValue: React.Key[]) => {
    setExpandedKeys(expandedKeysValue);
    setAutoExpandParent(false);
  };
  
  // Handle item selection
  const onSelect = (selectedKeys: React.Key[], info: any) => {
    if (onItemClick && info.node.isLeaf) {
      const itemId = String(selectedKeys[0]);
      const item = [...improvementItems, ...operationalItems].find(i => i.id === itemId);
      if (item) {
        onItemClick(item);
      }
    }
  };
  
  // Calculate default expanded keys (expand all artifact groups)
  const defaultExpandedKeys = useMemo(() => {
    const keys: string[] = [];
    [...improvementTreeData, ...operationalTreeData].forEach(node => {
      keys.push(String(node.key));
    });
    return keys;
  }, [improvementTreeData, operationalTreeData]);
  
  const hasItems = improvementItems.length > 0 || operationalItems.length > 0;
  
  if (!hasItems) {
    return (
      <Card className={`${styles.hierarchyContainer} ${className || ''}`} bordered={false}>
        <Empty 
          description="No artifact items linked to this objective"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }
  
  return (
    <Card className={`${styles.hierarchyContainer} ${className || ''}`} bordered={false}>
      {improvementItems.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <RocketOutlined className={styles.sectionIcon} style={{ color: '#1890ff' }} />
            <Title level={5} className={styles.sectionTitle}>
              Improvement Items
            </Title>
            <Badge 
              count={improvementItems.length} 
              style={{ backgroundColor: '#1890ff' }}
            />
          </div>
          <Tree
            treeData={improvementTreeData}
            onExpand={onExpand}
            expandedKeys={expandedKeys.length > 0 ? expandedKeys : defaultExpandedKeys}
            autoExpandParent={autoExpandParent}
            onSelect={onSelect}
            showLine={{ showLeafIcon: false }}
            className={styles.tree}
          />
        </div>
      )}
      
      {operationalItems.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <SettingOutlined className={styles.sectionIcon} style={{ color: '#52c41a' }} />
            <Title level={5} className={styles.sectionTitle}>
              Operational Items
            </Title>
            <Badge 
              count={operationalItems.length} 
              style={{ backgroundColor: '#52c41a' }}
            />
          </div>
          <Tree
            treeData={operationalTreeData}
            onExpand={onExpand}
            expandedKeys={expandedKeys.length > 0 ? expandedKeys : defaultExpandedKeys}
            autoExpandParent={autoExpandParent}
            onSelect={onSelect}
            showLine={{ showLeafIcon: false }}
            className={styles.tree}
          />
        </div>
      )}
    </Card>
  );
};

export default ArtifactItemsHierarchy;
